import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { Eye, Edit, CheckCircle, Clock, AlertTriangle, FileText, User } from 'lucide-react';
import { useReviewTasks } from '@/hooks/useReviewTasks';
import { useCustomers } from '@/hooks/useCustomers';
import { SimplifiedPDFTextSelector } from './SimplifiedPDFTextSelector';
import SmartPDFViewer from './SmartPDFViewer';
import { CorrectionWorkflow } from './CorrectionWorkflow';
import { CustomerValidationProvider } from './CustomerValidationProvider';
import { SafeErrorBoundary } from './SafeErrorBoundary';

interface ReviewTask {
  id: string;
  bill_id?: string;
  queue_item_id?: string;
  customer_id?: string;
  task_type: string;
  status: string;
  priority: number;
  validation_failures: any[];
  missing_mandatory_fields: string[];
  original_data: Record<string, any>;
  reviewed_data?: Record<string, any>;
  reviewer_notes?: string;
  created_at: string;
  estimated_duration_minutes: number;
}

interface ReviewTaskManagerProps {
  tasks: ReviewTask[];
  onTaskUpdate?: () => void;
}

export const ReviewTaskManager: React.FC<ReviewTaskManagerProps> = ({
  tasks,
  onTaskUpdate
}) => {
  const [selectedTask, setSelectedTask] = useState<ReviewTask | null>(null);
  const [editingTask, setEditingTask] = useState<ReviewTask | null>(null);
  const [showCorrection, setShowCorrection] = useState(false);
  const [correctedData, setCorrectedData] = useState<Record<string, any>>({});
  
  const { assignTask, completeTask } = useReviewTasks();

  const handleViewTask = (task: ReviewTask) => {
    setSelectedTask(task);
  };

  const handleEditTask = (task: ReviewTask) => {
    setEditingTask(task);
    setSelectedTask(null);
  };

  const handleSaveCorrections = async (data: Record<string, any>, notes?: string) => {
    if (!editingTask) return;

    try {
      setCorrectedData(data);
      setEditingTask(null);
      setShowCorrection(true);
      
      toast({
        title: "Corrections saved",
        description: "Starting AI learning workflow...",
      });
    } catch (error) {
      toast({
        title: "Error saving corrections",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCorrectionComplete = () => {
    setShowCorrection(false);
    setCorrectedData({});
    onTaskUpdate?.();
    
    toast({
      title: "Review completed",
      description: "Task completed and AI learning applied successfully.",
    });
  };

  const getPriorityColor = (priority: number) => {
    if (priority >= 80) return 'bg-red-100 text-red-800';
    if (priority >= 60) return 'bg-orange-100 text-orange-800';
    return 'bg-blue-100 text-blue-800';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-blue-600" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-orange-600" />;
    }
  };

  // Validate tasks array to prevent React Error #301
  if (!Array.isArray(tasks)) {
    console.error('ReviewTaskManager: tasks prop is not an array:', tasks);
    return (
      <div className="text-center py-8 text-muted-foreground">
        <AlertTriangle className="h-12 w-12 mx-auto mb-4" />
        <p>Error loading review tasks</p>
      </div>
    );
  }

  const pendingTasks = tasks.filter(task => task?.status === 'pending');
  const inProgressTasks = tasks.filter(task => task?.status === 'in_progress');
  const completedTasks = tasks.filter(task => task?.status === 'completed');

  return (
    <SafeErrorBoundary fallback={<div>Error loading review task manager</div>}>
      <div className="space-y-6">
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Pending ({pendingTasks?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="in_progress" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              In Progress ({inProgressTasks?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="completed" className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Completed ({completedTasks?.length || 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="space-y-4">
            {pendingTasks.length > 0 ? (
              pendingTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onView={() => handleViewTask(task)}
                  onEdit={() => handleEditTask(task)}
                  getPriorityColor={getPriorityColor}
                  getStatusIcon={getStatusIcon}
                />
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="h-12 w-12 mx-auto mb-4" />
                <p>No pending review tasks</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="in_progress" className="space-y-4">
            {inProgressTasks.length > 0 ? (
              inProgressTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onView={() => handleViewTask(task)}
                  onEdit={() => handleEditTask(task)}
                  getPriorityColor={getPriorityColor}
                  getStatusIcon={getStatusIcon}
                />
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-4" />
                <p>No tasks in progress</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="completed" className="space-y-4">
            {completedTasks.length > 0 ? (
              completedTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onView={() => handleViewTask(task)}
                  onEdit={() => handleEditTask(task)}
                  getPriorityColor={getPriorityColor}
                  getStatusIcon={getStatusIcon}
                  isCompleted
                />
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4" />
                <p>No completed tasks</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Task Details Dialog */}
      <Dialog open={!!selectedTask} onOpenChange={() => setSelectedTask(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Task Details</DialogTitle>
            <DialogDescription>
              View detailed information about this review task including validation issues and missing fields.
            </DialogDescription>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="font-medium">Type:</span>
                  <Badge variant="outline" className="ml-2">{selectedTask.task_type}</Badge>
                </div>
                <div>
                  <span className="font-medium">Priority:</span>
                  <Badge className={`ml-2 ${getPriorityColor(selectedTask.priority)}`}>
                    {selectedTask.priority}
                  </Badge>
                </div>
              </div>
              
              {selectedTask.validation_failures.length > 0 && (
                <div>
                  <span className="font-medium">Validation Failures:</span>
                  <ul className="mt-2 space-y-1">
                    {selectedTask.validation_failures.map((failure, index) => (
                      <li key={index} className="text-sm text-destructive">
                        • {failure.message || JSON.stringify(failure)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {selectedTask.missing_mandatory_fields.length > 0 && (
                <div>
                  <span className="font-medium">Missing Fields:</span>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedTask.missing_mandatory_fields.map((field) => (
                      <Badge key={field} variant="destructive">{field}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2">
                <Button onClick={() => handleEditTask(selectedTask)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Task
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Editor Dialog with Customer Validation */}
      <Dialog open={!!editingTask} onOpenChange={() => setEditingTask(null)}>
        <DialogContent className="max-w-6xl h-[90vh]" aria-describedby="edit-review-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Review and Edit with Customer Validation
            </DialogTitle>
            <DialogDescription id="edit-review-description">
              Review and correct the extracted data for this bill. Select a customer and validate all required fields.
            </DialogDescription>
          </DialogHeader>
          {editingTask && (
            <CustomerValidationProvider 
              initialCustomer={editingTask.customer_id ? { id: editingTask.customer_id } as any : null}
            >
              <div className="space-y-4">
                {/* Simple PDF viewer - functionality moved to dedicated review page */}
                <div className="text-center p-4 border rounded-lg">
                  <p className="text-muted-foreground">For detailed review and editing, please use the dedicated review page.</p>
                  <Button 
                    onClick={() => setEditingTask(null)}
                    className="mt-2"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </CustomerValidationProvider>
          )}
        </DialogContent>
      </Dialog>

      {/* Correction Workflow Dialog */}
      <Dialog open={showCorrection} onOpenChange={() => setShowCorrection(false)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" aria-describedby="ai-learning-description">
          <DialogHeader>
            <DialogTitle>AI Learning Workflow</DialogTitle>
            <DialogDescription id="ai-learning-description">
              Complete the AI learning process by providing feedback on the corrections made to improve future processing.
            </DialogDescription>
          </DialogHeader>
          {selectedTask && (
            <CorrectionWorkflow
              billId={selectedTask.bill_id || ''}
              queueItemId={selectedTask.queue_item_id}
              originalData={selectedTask.original_data}
              correctedData={correctedData}
              onComplete={handleCorrectionComplete}
            />
          )}
        </DialogContent>
      </Dialog>
    </SafeErrorBoundary>
  );
};

interface TaskCardProps {
  task: ReviewTask;
  onView: () => void;
  onEdit: () => void;
  getPriorityColor: (priority: number) => string;
  getStatusIcon: (status: string) => React.ReactNode;
  isCompleted?: boolean;
}

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onView,
  onEdit,
  getPriorityColor,
  getStatusIcon,
  isCompleted = false
}) => {
  // Validate task object to prevent React Error #301
  if (!task || typeof task !== 'object' || !task.id) {
    console.error('TaskCard: Invalid task object:', task);
    return (
      <Card>
        <CardContent className="p-4">
          <div className="text-destructive">Invalid task data</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <SafeErrorBoundary fallback={<Card><CardContent className="p-4">Error loading task</CardContent></Card>}>
      <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {getStatusIcon(task.status)}
              <span className="font-medium">{task.task_type.replace(/_/g, ' ').toUpperCase()}</span>
              <Badge className={getPriorityColor(task.priority)}>
                Priority {task.priority}
              </Badge>
            </div>
            
            <div className="text-sm text-muted-foreground">
              Created: {new Date(task.created_at).toLocaleDateString()}
              {task.estimated_duration_minutes && (
                <span className="ml-4">Est. {task.estimated_duration_minutes}min</span>
              )}
            </div>
            
            {task.validation_failures.length > 0 && (
              <div className="text-sm">
                <span className="text-destructive">{task.validation_failures.length} validation errors</span>
              </div>
            )}
            
            {task.missing_mandatory_fields.length > 0 && (
              <div className="text-sm">
                <span className="text-orange-600">{task.missing_mandatory_fields.length} missing fields</span>
              </div>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onView}>
              <Eye className="h-4 w-4" />
            </Button>
            {!isCompleted && (
              <Button size="sm" onClick={onEdit}>
                <Edit className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
    </SafeErrorBoundary>
  );
};